import React from 'react';
import {
  ButtonContainer,
  ButtonIconContainer,
  ButtonIconExternalLinkContainer,
  ButtonIconLinkContainer,
} from './Button.styles';
import { InternalLinkContainer } from './Button.styles';
import { ExternalLinkContainer } from './Button.styles';
import { urlIsExternal } from 'src/functions/propagateQueryParams';
import useBaseLink from 'src/hooks/useBaseLink';
import { ButtonProps } from './Button';

type ButtonActionWrapperProps = Pick<
  ButtonProps,
  | 'variant'
  | 'size'
  | 'href'
  | 'onClick'
  | 'disabled'
  | 'type'
  | 'className'
  | 'children'
  | 'radius'
  | 'decoration'
  | 'display'
  | 'noWrap'
  | 'data-track-id'
  | 'data-track-type'
> & {
  isIconOnly?: boolean;
};

const ButtonActionWrapper = ({
  variant,
  size,
  href,
  onClick,
  disabled,
  type,
  className,
  children,
  isIconOnly = false,
  radius,
  decoration,
  display,
  noWrap,
  'data-track-id': dataTrackId,
  'data-track-type': dataTrackType,
}: ButtonActionWrapperProps): JSX.Element => {
  const buttonClasses = `${disabled ? 'disabled' : ''} ${className || ''}`;

  const globalProperties = {
    $decoration: decoration,
    $variant: variant,
    $size: size,
    $radius: radius,
    $display: display,
    $noWrap: noWrap,
    className: buttonClasses,
    'data-cy': 'button-atom',
    'data-track-id': dataTrackId,
    'data-track-type': dataTrackType,
  };

  const containers = {
    regular: {
      button: ButtonContainer,
      link: InternalLinkContainer,
      externalLink: ExternalLinkContainer,
    },
    iconOnly: {
      button: ButtonIconContainer,
      link: ButtonIconLinkContainer,
      externalLink: ButtonIconExternalLinkContainer,
    },
  };

  const container = isIconOnly ? containers.iconOnly : containers.regular;

  const renderAsExternalLink = () => {
    const Container = container.externalLink;
    const linkProps = useBaseLink(href, onClick);

    return (
      <Container
        {...globalProperties}
        {...linkProps}
      >
        {children}
      </Container>
    );
  };

  const renderAsInternalLink = () => {
    const linkProps = useBaseLink(href, onClick);
    const Container = container.link;

    return (
      <Container
        {...globalProperties}
        onClick={linkProps.onClick}
        to={linkProps.href}
      >
        {children}
      </Container>
    );
  };

  const renderAsButton = () => {
    const Container = container.button;

    return (
      <Container
        {...globalProperties}
        onClick={onClick}
        type={type}
        disabled={disabled}
      >
        {children}
      </Container>
    );
  };

  const renderAsLink = () => {
    if (urlIsExternal(href)) {
      return renderAsExternalLink();
    } else {
      return renderAsInternalLink();
    }
  };

  if (href) {
    return renderAsLink();
  }

  return renderAsButton();
};

export default ButtonActionWrapper;
